import { createPatch } from "rfc6902";
import { ArsContextState, ArsDataState, ArsPatch } from "../interface";

/**
 * Extracts mutable ARS data from context state, excluding undo/redo metadata.
 *
 * @param state - The full ARS context state
 * @returns Data-only state used for patch generation
 */
export const extractDataState = <T>(state: ArsContextState<T>): ArsDataState<T> => ({
  analyses: state.analyses,
  analysisMethods: state.analysisMethods,
  analysisSets: state.analysisSets,
  caseWhenConditions: state.caseWhenConditions,
  changed: state.changed,
  comparators: state.comparators,
  dataSubsets: state.dataSubsets,
  datasetsInUse: state.datasetsInUse,
  disableAnalysisMethodEdit: state.disableAnalysisMethodEdit,
  editorDefns: state.editorDefns,
  groupingFactors: state.groupingFactors,
  uiRequestStatus: state.uiRequestStatus,
  variables: state.variables,
  variablesInUse: state.variablesInUse,
});

/**
 * Adds a reversible patch entry for a state transition.
 *
 * @param previousState - State before mutation
 * @param newState - State after mutation
 * @returns Updated state with patch metadata appended when data changed
 */
export const addPatchToStack = <T>(
  previousState: ArsContextState<T>,
  newState: ArsContextState<T>,
): ArsContextState<T> => {
  const previousData = extractDataState(previousState);
  const newData = extractDataState(newState);

  const forwardPatch = createPatch(previousData, newData);
  const reversePatch = createPatch(newData, previousData);

  if (forwardPatch.length === 0) {
    return newState;
  }

  const newPatch: ArsPatch = {
    forward: forwardPatch,
    reverse: reversePatch,
  };

  const newPatches = [...previousState.patches.slice(0, previousState.ixPatch + 1), newPatch];

  return {
    ...newState,
    patches: newPatches,
    ixPatch: newPatches.length - 1,
  };
};
